// Buttons with a tactile lip that compresses on press, like a real bedside call button.
// primary (scrubs teal), cart (crash-cart orange, navy text, for the loudest call to action), quiet (white),
// danger (rose), ghost (no surface). Full width by default; pass a width utility (w-auto, w-40…) to change it.
// done: the action worked (M1, the join): the button turns emerald at full strength and takes no more taps, so the
// check it shows never looks greyed out the way a disabled button does.
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'cart' | 'quiet' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'rd-btn-primary text-white',
  cart: 'rd-btn-cart text-ink',
  quiet: 'rd-btn-quiet text-ink',
  danger: 'rd-btn-danger text-white',
  ghost: 'rd-btn-ghost text-ink-2',
};
const SIZE: Record<ButtonSize, string> = {
  sm: 'min-h-10 px-4 text-[0.95rem] gap-1.5 rounded-[14px]',
  md: 'min-h-12 px-5 text-base gap-2 rounded-[16px]',
  lg: 'min-h-14 px-6 text-lg gap-2.5 rounded-[var(--r-tile)]',
};

/** The button's classes, for a link that should look like one (a page header's "New battle"). Same width rule as the button. */
export function buttonClass({ variant = 'primary', size = 'lg', done = false, className = '' }: { variant?: ButtonVariant; size?: ButtonSize; done?: boolean; className?: string } = {}) {
  const width = /(^|\s)(w-|inline-flex|flex-1)/.test(className) ? '' : 'w-full';
  return `rd-btn relative inline-flex select-none items-center justify-center font-ui font-bold tracking-[-0.005em] ${width} ${SIZE[size]} ${done ? 'rd-btn-done text-white' : VARIANT[variant]} ${className}`;
}

export function Button({ className = '', variant = 'primary', size = 'lg', busy = false, done = false, icon, children, disabled, ...p }: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant; size?: ButtonSize;
  /** Shows a small pulse and keeps the label (screen readers hear aria-busy). */
  busy?: boolean;
  /** The action succeeded: emerald, full strength, no more taps. */
  done?: boolean;
  /** An icon before the label. */
  icon?: ReactNode;
}) {
  return (
    <button type="button" {...p} disabled={disabled || done} aria-busy={busy || undefined} data-done={done || undefined}
      className={buttonClass({ variant, size, done, className })}>
      {icon && <span aria-hidden="true" className="inline-flex shrink-0">{icon}</span>}
      <span className="min-w-0">{children}</span>
      {busy && <span aria-hidden="true" className="rd-btn-busy ml-1 inline-flex gap-1"><i /><i /><i /></span>}
    </button>
  );
}
